import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Star } from 'lucide-react'
import { useStore } from '../store/useStore'
import { showToast } from '../components/Toast'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import ShareButtonsRow from '../components/ShareButtonsRow'

const TAG_KEYS = [
  { emoji: '\uD83C\uDF7D\uFE0F', key: 'review.greatFood' },
  { emoji: '\uD83D\uDE0A', key: 'review.kindStaff' },
  { emoji: '\uD83D\uDCF8', key: 'review.photogenic' },
  { emoji: '\uD83D\uDCB0', key: 'review.goodValue' },
  { emoji: '\uD83C\uDF3F', key: 'review.cozyVibe' },
  { emoji: '\uD83D\uDD04', key: 'review.willReturn' },
]

export default function Review() {
  const navigate = useNavigate()
  const { currentStore } = useStore()
  const { t } = useI18n()

  const [rating, setRating] = useState(0)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const storeName = currentStore?.name || 'うなぎ 平塚屋'

  const toggleTag = (label: string) => {
    setSelectedTags((prev) =>
      prev.includes(label)
        ? prev.filter((t) => t !== label)
        : [...prev, label]
    )
  }

  const handleSubmit = async () => {
    if (submitting || rating === 0) return
    setSubmitting(true)
    try {
      const storeId = currentStore?.id || 'store_001'
      await api.submitReview(storeId, rating, selectedTags, comment)
      setSubmitted(true)
    } catch (err) {
      console.error('[Review] Submit failed:', err)
      showToast({ type: 'error', message: t('review.submitFailed') })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-warm-white safe-top safe-bottom">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <button
          onClick={() => navigate(-1)}
          className="tap-effect flex h-10 w-10 items-center justify-center rounded-xl"
        >
          <ArrowLeft size={22} className="text-text-primary" />
        </button>
        <h1 className="font-display text-lg font-semibold text-text-primary">
          {t('review.title')}
        </h1>
        <button
          onClick={() => navigate('/home')}
          className="tap-effect font-body text-sm font-medium text-text-secondary"
        >
          {t('review.skip')}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {submitted ? (
          /* ── Post-submission: Thank you + SNS share ── */
          <div className="flex flex-col items-center pt-10">
            <p className="text-5xl">🎉</p>
            <h2 className="mt-4 font-display text-xl font-bold text-text-primary">
              {t('review.thanks')}
            </h2>
            <p className="mt-2 text-center font-body text-sm text-text-secondary">
              {storeName}
            </p>

            {/* Star display */}
            <div className="mt-4 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  size={24}
                  className={rating >= star ? 'fill-gold text-gold' : 'fill-none text-gray-300'}
                  strokeWidth={1.5}
                />
              ))}
            </div>

            {/* Divider */}
            <div className="mt-8 w-full border-t border-gray-200" />

            {/* SNS Share Section */}
            <h3 className="mt-6 font-display text-base font-semibold text-text-primary">
              {t('review.sharePrompt')}
            </h3>
            <div className="mt-4 w-full">
              <ShareButtonsRow
                storeId={currentStore?.id || 'store_001'}
                storeName={storeName}
                snsMedia={currentStore?.snsMedia}
                source="review"
              />
            </div>

            {/* Done Button */}
            <button
              onClick={() => navigate('/home')}
              className="tap-effect mt-6 h-14 w-full rounded-xl bg-navy font-display text-base font-semibold text-white transition-colors hover:bg-navy-dark"
            >
              {t('review.done')}
            </button>
          </div>
        ) : (
          /* ── Review form ── */
          <>
            {/* Heading */}
            <h2 className="mt-4 text-center font-display text-xl font-bold text-text-primary">
              {t('review.howWas')}
            </h2>
            <p className="mt-1 text-center font-jp text-base text-text-secondary">
              {storeName}
            </p>

            {/* Star Rating */}
            <div className="mt-6 flex items-center justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="tap-effect transition-transform"
                  style={{
                    transform: rating >= star ? 'scale(1.15)' : 'scale(1)',
                    transition: 'transform 0.15s ease-out',
                  }}
                >
                  <Star
                    size={48}
                    className={
                      rating >= star
                        ? 'fill-gold text-gold'
                        : 'fill-none text-gray-300'
                    }
                    strokeWidth={1.5}
                  />
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="mx-0 mt-6 border-t border-gray-200" />

            {/* Quick Tags */}
            <h3 className="mt-5 font-display text-base font-semibold text-text-primary">
              {t('review.quickTags')}
            </h3>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {TAG_KEYS.map((tag) => {
                const label = t(tag.key)
                const isSelected = selectedTags.includes(tag.key)
                return (
                  <button
                    key={tag.key}
                    onClick={() => toggleTag(tag.key)}
                    className={`tap-effect rounded-xl border px-4 py-3 text-sm transition-colors ${
                      isSelected
                        ? 'border-yokoso-red bg-yokoso-red text-white'
                        : 'border-gray-200 bg-white text-text-primary'
                    }`}
                  >
                    {tag.emoji} {label}
                  </button>
                )
              })}
            </div>

            {/* Divider */}
            <div className="mx-0 mt-6 border-t border-gray-200" />

            {/* Comment */}
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('review.comment')}
              className="mt-5 h-24 w-full resize-none rounded-xl border border-gray-200 bg-white p-4 font-body text-sm text-text-primary placeholder:text-text-disabled focus:border-yokoso-red focus:outline-none focus:ring-1 focus:ring-yokoso-red"
            />

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={submitting || rating === 0}
              className="tap-effect mt-5 h-14 w-full rounded-xl bg-yokoso-red font-display text-base font-semibold text-white shadow-button transition-colors hover:bg-yokoso-red-dark disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? '...' : t('review.submit')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
